// utils/theme.ts

// Define a type for color variants
export type ColorVariant = 'DEFAULT' | 'dark' | 'light';

// Define a type for the structure of each color group
export interface ColorGroup {
    DEFAULT: string;
    dark?: string;  // Optional, as not all colors have all variants
    light?: string; // Optional
}

// Define the theme object with explicit types
export const theme = {
    colors: {
        // These match exactly with Tailwind config names
        primary: {
            DEFAULT: "primary",
            dark: "primary-dark",
            light: "primary-light",
        } as ColorGroup,
        secondary: {
            DEFAULT: "secondary",
            dark: "secondary-dark",
            light: "secondary-light",
        } as ColorGroup,
        gray: {
            50: "gray-50",
            100: "gray-100",
            200: "gray-200",
            300: "gray-300",
            400: "gray-400",
            500: "gray-500",
            600: "gray-600",
            700: "gray-700",
            800: "gray-800",
            850: "gray-850",
            900: "gray-900",
        },
        green: {
            DEFAULT: "green",
            dark: "green-dark",
        } as ColorGroup,
        yellow: {
            DEFAULT: "yellow",
            dark: "yellow-dark",
        } as ColorGroup,
        orange: {
            DEFAULT: "orange",
            dark: "orange-dark",
        } as ColorGroup,
        purple: {
            DEFAULT: "purple",
            dark: "purple-dark",
        } as ColorGroup,
        pink: {
            DEFAULT: "pink",
            dark: "pink-dark",
        } as ColorGroup,
        teal: {
            DEFAULT: "teal",
            dark: "teal-dark",
        } as ColorGroup,
        blue: {
            DEFAULT: "blue",
        } as ColorGroup,
        error: {
            DEFAULT: "error",
            dark: "error-dark",
        } as ColorGroup,
        success: {
            DEFAULT: "success",
            dark: "success-dark",
        } as ColorGroup,
        beige: {
            DEFAULT: "beige",
            dark: "beige-dark",
            light: "beige-light",
        } as ColorGroup,
        surface: {
            light: "surface-light",
        } as ColorGroup,
    },
    fonts: {
        heading: "heading",
        body: "body",
    },
};

// Define a type for valid color names
export type ColorName = keyof typeof theme.colors;

// Type guard function to check for valid color name
export function isValidColorName(colorName: string): colorName is ColorName {
    return colorName in theme.colors;
}

// Type guard function to check for valid color variant.
export function isValidColorVariant(variant: string): variant is ColorVariant {
    return ['DEFAULT', 'dark', 'light'].includes(variant);
}